import React, { useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { CONTROL_INPUT, MUTED_LABEL } from './safety-ui.js';
import { useSafetyWrite } from './use-safety-write.js';
import type { TerminalOption } from './use-safety-read-model.js';

interface SafetyMeetingOpenProps {
  terminalOptions: TerminalOption[];
  onSuccess: (meetingId?: string | number | null) => void;
}

export default function SafetyMeetingOpen({ terminalOptions, onSuccess }: SafetyMeetingOpenProps) {
  const [showForm, setShowForm] = useState(false);
  const [terminal, setTerminal] = useState('');
  const [topic, setTopic] = useState('');
  const { openMeeting, submitting, error, clearError } = useSafetyWrite((meeting) => {
    setShowForm(false);
    setTerminal('');
    setTopic('');
    onSuccess(meeting?.id ?? null);
  });

  function handleSubmit() {
    if (!terminal || !topic.trim()) return;
    openMeeting(terminal, topic.trim());
  }

  if (!showForm) {
    return (
      <button
        type="button"
        onClick={() => { setShowForm(true); clearError(); }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 16px',
          borderRadius: 8,
          background: C.green,
          color: '#111',
          border: 'none',
          fontWeight: 700,
          fontSize: 13,
          cursor: 'pointer',
          marginBottom: 12,
        }}
      >
        Open Safety Meeting
      </button>
    );
  }

  // Filter out "all" from terminal options — need a real terminal for the meeting
  const terminals = terminalOptions.filter((t) => t.id !== 'all');

  const canSubmit = !!terminal && topic.trim().length > 0 && !submitting;

  return (
    <div style={{ ...LAYOUT.subPanel, marginBottom: 12 }}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.green, marginBottom: 10 }}>
        Open new safety meeting
      </div>

      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 2fr', marginBottom: 12 }}>
        <div>
          <label style={MUTED_LABEL}>Terminal *</label>
          <select
            value={terminal}
            onChange={(e) => setTerminal(e.target.value)}
            style={CONTROL_INPUT}
          >
            <option value="">Select terminal...</option>
            {terminals.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={MUTED_LABEL}>Topic *</label>
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) handleSubmit(); }}
            placeholder="e.g. Weekly safety review, Incident debrief"
            style={CONTROL_INPUT}
          />
        </div>
      </div>

      {error && (
        <div style={{
          color: C.red,
          fontSize: 13,
          marginBottom: 10,
          padding: '8px 12px',
          borderRadius: 8,
          background: 'rgba(255,107,107,0.1)',
          border: '1px solid rgba(255,107,107,0.2)',
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{
            padding: '10px 20px',
            borderRadius: 8,
            background: canSubmit ? C.green : 'rgba(255,255,255,0.06)',
            color: canSubmit ? '#111' : 'rgba(255,255,255,0.2)',
            border: 'none',
            fontWeight: 700,
            fontSize: 13,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
          }}
        >
          {submitting ? 'Opening...' : 'Open Meeting'}
        </button>
        <button
          type="button"
          onClick={() => { setShowForm(false); clearError(); }}
          style={{
            padding: '10px 16px',
            borderRadius: 8,
            background: 'none',
            color: C.textMuted,
            border: `1px solid ${C.borderSoft}`,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
